// src/components/LandingPage.js
//
// The public page at /. Every claim here is something the product does today;
// the Basic / Pro table is computed from constants/plans.js so it cannot drift
// from what the API enforces. Screenshots are of the Green Point Logistics demo
// workspace (docs/user-guide/landing_capture.py); photos marked as such are
// illustrations, not product UI.
import React from 'react';
import {
  Box, Button, Container, Grid, Stack, Typography, Link as MuiLink,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  Check as CheckIcon,
  Remove as DashIcon,
  EditNote as FormIcon,
  PhoneAndroid as PhoneIcon,
  FactCheck as ReviewIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { TIERS } from '../constants/plans';
import ContactForm from './ContactForm';

const GREEN_DARK = '#0d3320';
const GREEN = '#137a3e';
const INK = '#16231b';

const img = (name) => `${process.env.PUBLIC_URL || ''}/landing/${name}.webp`;

function Screenshot({ name, alt, sx }) {
  return (
    <Box
      component="img"
      src={img(name)}
      alt={alt}
      loading="lazy"
      sx={{
        display: 'block',
        width: '100%',
        height: 'auto',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'rgba(13, 51, 32, 0.12)',
        boxShadow: '0 18px 50px rgba(13, 51, 32, 0.14)',
        ...sx,
      }}
    />
  );
}

// `feature` names the tier entitlement the section describes, so the Pro
// chip is read from the same table as the plan comparison below rather than
// hand-set: a feature that moves tier cannot leave a stale chip behind.
function Feature({ eyebrow, title, children, image, alt, feature, reverse }) {
  const isPro = Boolean(feature) && TIERS.basic.entitlements[feature] !== true;
  return (
    <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center" direction={{ xs: 'column', md: reverse ? 'row-reverse' : 'row' }}>
      <Grid size={{ xs: 12, md: 5 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="overline" sx={{ color: GREEN, fontWeight: 700, letterSpacing: '0.1em' }}>
            {eyebrow}
          </Typography>
          {isPro && <Chip label="Pro" size="small" sx={{ bgcolor: GREEN, color: 'white', fontWeight: 700, height: 20 }} />}
        </Stack>
        <Typography variant="h4" component="h3" sx={{ fontWeight: 800, color: INK, mb: 2, fontSize: { xs: '1.6rem', md: '2rem' }, lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography component="div" variant="body1" sx={{ color: 'text.secondary', fontSize: '1.05rem', lineHeight: 1.75 }}>
          {children}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 7 }} sx={{ width: '100%' }}>
        <Screenshot name={image} alt={alt} />
      </Grid>
    </Grid>
  );
}

// Rows of the plan comparison. `feature` reads the tier's entitlement; rows
// without one are part of every plan.
const PLAN_ROWS = [
  { label: 'Phone app for your operators, works without signal' },
  { label: 'Your own forms and photo checklists' },
  { label: 'Watermarked photos with time, place and operator' },
  { label: 'Review, approve and export to PDF, Excel or ZIP' },
  { label: 'Customer portal', feature: 'customer_portal' },
  { label: 'Search across all your tasks', feature: 'full_text_search' },
  { label: 'Scheduled email reports', reports: true },
  { label: 'Assignments sent to operators ahead of time', feature: 'assignments' },
  { label: 'Bulk export of whole months', feature: 'mass_export' },
  { label: 'Assets, QR codes and issue tracking with deadlines', feature: 'asset_tracking' },
  { label: 'Photos saved to Google Drive or OneDrive, and webhooks', feature: 'integrations' },
  { label: 'Analytics dashboard', feature: 'advanced_analytics' },
  { label: 'Sign in with Microsoft', feature: 'sso' },
  { label: 'API access', feature: 'api_access' },
];

function reportsText(tier) {
  const r = TIERS[tier].scheduledReports;
  if (r.maxDaily == null && r.maxWeekly == null) return `Unlimited · ${r.maxRecipients} recipients`;
  return `${r.maxDaily} daily, ${r.maxWeekly} weekly · ${r.maxRecipients} recipients`;
}

function PlanCell({ tier, row }) {
  if (row.reports) {
    return <Typography variant="body2" sx={{ fontWeight: 600, color: INK }}>{reportsText(tier)}</Typography>;
  }
  const included = !row.feature || TIERS[tier].entitlements[row.feature] === true;
  return included
    ? <CheckIcon aria-label="Included" sx={{ color: GREEN }} />
    : <DashIcon aria-label="Not included" sx={{ color: 'grey.400' }} />;
}

function LandingPage() {
  const navigate = useNavigate();

  const trialButton = (label, sx) => (
    <Button
      variant="contained"
      size="large"
      onClick={() => navigate('/start-trial')}
      endIcon={<ArrowIcon />}
      sx={{
        py: 1.5, px: 4, bgcolor: 'white', color: GREEN, fontWeight: 700, fontSize: '1rem',
        borderRadius: 2, textTransform: 'none', boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
        '&:hover': { bgcolor: '#f3f6f4' },
        ...sx,
      }}
    >
      {label}
    </Button>
  );

  return (
    <Box sx={{ bgcolor: '#f7f9f7', minHeight: '100vh', color: INK }}>
      {/* ------------------------------------------------------------ Hero */}
      <Box sx={{ background: `linear-gradient(160deg, ${GREEN_DARK} 0%, ${GREEN} 70%)`, color: 'white', pt: { xs: 7, md: 11 }, pb: { xs: 12, md: 18 } }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 760, mx: 'auto', textAlign: 'center' }}>
            <Typography variant="overline" sx={{ letterSpacing: '0.18em', opacity: 0.85, fontWeight: 700 }}>
              TrakLog
            </Typography>
            <Typography variant="h2" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2.3rem', md: '3.6rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', mt: 1, mb: 2.5 }}>
              Proof of work from the field, in minutes.
            </Typography>
            <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' }, opacity: 0.9, lineHeight: 1.6, mb: 4 }}>
              Your operators record each job on an Android phone: photos, a short form and the location.
              It shows up on the web straight away, ready to review, export or share with your customer.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              {trialButton('Start a 30-day free trial')}
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{ py: 1.5, px: 4, color: 'white', borderColor: 'rgba(255,255,255,0.55)', fontWeight: 600, fontSize: '1rem', borderRadius: 2, textTransform: 'none', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
              >
                Sign in
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
      <Container maxWidth="lg" sx={{ mt: { xs: -9, md: -13 } }}>
        <Box component="img" src={img('tasks')} alt="The TrakLog task list: a container unstuffing and a delivery, each with photos, operator, location and form answers" sx={{ display: 'block', width: '100%', height: 'auto', borderRadius: 3, border: '1px solid rgba(13,51,32,0.12)', boxShadow: '0 30px 80px rgba(13, 51, 32, 0.25)' }} />
      </Container>

      {/* ------------------------------------------------------ How it works */}
      <Container maxWidth="lg" sx={{ py: { xs: 9, md: 12 } }}>
        <Typography variant="h3" component="h2" sx={{ fontWeight: 800, textAlign: 'center', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: { xs: 5, md: 7 } }}>
          How it works
        </Typography>
        <Grid container spacing={4}>
          {[
            { icon: <FormIcon />, title: 'Set up a form', text: 'Choose the fields and photos each kind of job needs, such as a container, a delivery or a daily check.' },
            { icon: <PhoneIcon />, title: 'Record the job on the phone', text: 'Operators sign in with a PIN, follow the checklist and submit. No signal? It sends when the phone is back online.' },
            { icon: <ReviewIcon />, title: 'Review and share', text: 'See each job on the web as it arrives. Approve it, export it, or let your customer see it in their own portal.' },
          ].map((s, i) => (
            <Grid key={s.title} size={{ xs: 12, md: 4 }}>
              <Stack spacing={1.5}>
                <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: '#e3f1e8', color: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {s.icon}
                </Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
                  {i + 1}. {s.title}
                </Typography>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{s.text}</Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ------------------------------------------------------ Photo band */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box component="img" src={img('photo-container')} alt="" loading="lazy" sx={{ display: 'block', width: '100%', height: { xs: 280, md: 460 }, objectFit: 'cover' }} />
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(13,51,32,0.85) 0%, rgba(13,51,32,0.35) 55%, transparent 100%)', display: 'flex', alignItems: 'center' }}>
          <Container maxWidth="lg">
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: { xs: '1.5rem', md: '2.4rem' }, lineHeight: 1.2, maxWidth: 520 }}>
              Made for container yards, warehouses, fleets and deliveries.
            </Typography>
          </Container>
        </Box>
      </Box>

      {/* ------------------------------------------------------ Features */}
      <Container maxWidth="lg" sx={{ py: { xs: 9, md: 12 } }}>
        <Stack spacing={{ xs: 10, md: 14 }}>
          <Feature
            eyebrow="Photos you can rely on"
            title="Every photo says when, where and who"
            image="task-detail"
            alt="A task with five labelled photos, the form answers, and a history of when each photo was taken"
          >
            Each photo is stamped with the time, the location and the operator, and filed under the step it
            belongs to. The phone warns when a photo is too blurry to use. Every task keeps a history of what
            happened and when.
          </Feature>

          <Feature
            eyebrow="Your own forms"
            title="Checklists that match how you work"
            image="template-editor"
            alt="The template editor: photo categories on the left and a preview of what the operator sees on the right"
            reverse
          >
            Build a form for each kind of job: the questions to answer, the photos to take and how many.
            A preview shows exactly what the operator will see on the phone. The app itself comes in eight languages.
          </Feature>

          <Feature
            eyebrow="Assignments"
            title="Send out the work before it starts"
            image="assignments"
            alt="The assignments list: three delivery orders waiting for operators"
            feature="assignments"
          >
            Create jobs one at a time or upload a spreadsheet. They appear in the operator's inbox with details
            already filled in, and come back as a completed task.
          </Feature>

          <Feature
            eyebrow="Assets and issues"
            title="Keep track of equipment and what's wrong with it"
            image="assets"
            alt="The assets list with forklift and reefer containers, their status and open issues"
            reverse
            feature="asset_tracking"
          >
            Every forklift, container or machine keeps its own history. Stick a QR code on it so anyone can report
            a problem, and see how long each issue has left before its deadline.
          </Feature>

          <Feature
            eyebrow="Analytics"
            title="See how the work is going"
            image="analytics"
            alt="The analytics dashboard: tasks, photos, approval rate and activity over the last 30 days"
            feature="advanced_analytics"
          >
            Tasks, photos, approval rate and issue deadlines over the last month, quarter or year.
            Reports can also arrive by email every day or every week.
          </Feature>
        </Stack>
      </Container>

      {/* ------------------------------------------------------ Customer strip */}
      <Box sx={{ bgcolor: 'white', py: { xs: 9, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Grid container spacing={2}>
                <Grid size={6}><Box component="img" src={img('photo-delivery')} alt="" loading="lazy" sx={{ width: '100%', height: 'auto', borderRadius: 2, display: 'block' }} /></Grid>
                <Grid size={6}><Box component="img" src={img('photo-qr')} alt="" loading="lazy" sx={{ width: '100%', height: 'auto', borderRadius: 2, display: 'block', mt: { xs: 3, md: 6 } }} /></Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="overline" sx={{ color: GREEN, fontWeight: 700, letterSpacing: '0.1em' }}>
                Customer portal
              </Typography>
              <Typography variant="h4" component="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.6rem', md: '2rem' } }}>
                Your customers see their own jobs and nothing else
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '1.05rem', lineHeight: 1.75 }}>
                Give a customer a login and they can look up the work you did for them, with the photos.
                You decide when a job becomes visible: straight away, after a delay, or once you've approved it.
                You can also email a task to anyone as a PDF.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ------------------------------------------------------ Plans */}
      <Container maxWidth="md" sx={{ py: { xs: 9, md: 12 } }}>
        <Typography variant="h3" component="h2" sx={{ fontWeight: 800, textAlign: 'center', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
          Basic or Pro
        </Typography>
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', mb: 5, fontSize: '1.05rem' }}>
          Both come with 100 GB or 500 GB of storage, and more can be added.
        </Typography>
        {/* Both plan columns must stay visible at phone width, so the table
            shrinks its padding and type rather than scrolling sideways. */}
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 3 }}>
          <Table sx={{ '& td, & th': { px: { xs: 1.25, sm: 2 }, py: { xs: 1.25, sm: 1.75 }, fontSize: { xs: '0.85rem', sm: '0.95rem' } } }}>
            <TableHead>
              <TableRow>
                <TableCell />
                {['basic', 'pro'].map((t) => (
                  <TableCell key={t} align="center" sx={{ fontWeight: 800, color: t === 'pro' ? GREEN : INK, width: { xs: 88, sm: '24%' }, '&&': { fontSize: { xs: '1rem', sm: '1.1rem' } } }}>
                    {TIERS[t].name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {PLAN_ROWS.map((row) => (
                <TableRow key={row.label}>
                  <TableCell sx={{ color: INK }}>{row.label}</TableCell>
                  {['basic', 'pro'].map((t) => (
                    <TableCell key={t} align="center"><PlanCell tier={t} row={row} /></TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', mt: 3 }}>
          The free trial includes everything in Pro, with 5 GB of storage, for 30 days.{' '}
          <MuiLink href="#contact" sx={{ color: GREEN, fontWeight: 600 }}>
            Ask us for pricing
          </MuiLink>.
        </Typography>
      </Container>

      {/* ------------------------------------------------------ Contact */}
      <Box id="contact" sx={{ bgcolor: 'white', py: { xs: 9, md: 12 }, scrollMarginTop: 72 }}>
        <Container maxWidth="sm">
          <Typography variant="h3" component="h2" sx={{ fontWeight: 800, textAlign: 'center', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
            Talk to us
          </Typography>
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', mb: 4, fontSize: '1.05rem' }}>
            Pricing, a demo, or whether TrakLog fits the way you work. We reply by email.
          </Typography>
          <ContactForm />
        </Container>
      </Box>

      {/* ------------------------------------------------------ Closing CTA */}
      <Box sx={{ background: `linear-gradient(160deg, ${GREEN_DARK} 0%, ${GREEN} 80%)`, color: 'white', py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 1.5, fontSize: { xs: '1.6rem', md: '2.1rem' } }}>
            Try it on your next job
          </Typography>
          <Typography sx={{ opacity: 0.9, mb: 4, lineHeight: 1.6 }}>
            Set up a form, add an operator and record a real job the same day.
          </Typography>
          {trialButton('Start a 30-day free trial')}
        </Container>
      </Box>

      {/* ------------------------------------------------------ Footer */}
      <Box sx={{ bgcolor: 'white', py: 3, borderTop: 1, borderColor: 'grey.200' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" textAlign="center">
            &copy; {new Date().getFullYear()} TrakLog
            {' '}&bull;{' '}
            <MuiLink component={RouterLink} to="/privacy-policy" color="text.secondary" underline="hover">
              Privacy Policy
            </MuiLink>
            {' '}&bull;{' '}
            <MuiLink component={RouterLink} to="/login" color="text.secondary" underline="hover">
              Sign in
            </MuiLink>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage;
